import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import _ from "lodash";

import Slider from "src/components/slider";
import Button from "src/components/button";
import {
  deleteReward,
  invalidateDeleteRewardState,
} from "src/redux/actions/coaching/gamification";
import { RewardResponse } from "src/types/coaching/gamification";

import styles from "./deleteRewardSlider.module.scss";

export interface DeleteRewardSliderProps {
  show: boolean;
  reward: RewardResponse | null;
  onFinish: () => void;
  onClose: () => void;
}

export const DeleteRewardSlider: React.FC<DeleteRewardSliderProps> = ({
  show,
  reward,
  onFinish,
  onClose,
}) => {
  const dispatch = useDispatch();

  const rewardId = _.get(reward, "id");
  const hasPendingRedemptions = _.size(_.get(reward, "pendingRedemptionIds")) > 0;

  const deleting = useSelector((state: any) =>
    show && _.get(state.coaching, `gamification.deleteReward['${rewardId}'].deleting`) as boolean
  );

  const deleted = useSelector((state: any) =>
    show && _.get(state.coaching, `gamification.deleteReward['${rewardId}'].deleted`) as boolean
  );

  const error = useSelector((state: any) =>
    show && _.get(state.coaching, `gamification.deleteReward['${rewardId}'].error`)
  );

  const isPendingRedemptionError = _.get(error, 'errors[0].code') === 11002;

  useEffect(() => {
    if (deleted) {
      const id = _.get(reward, "id");
      if (id) {
        dispatch(invalidateDeleteRewardState(id));
        onFinish();
      }
    }
  }, [deleted]);

  const handleConfirm = () => {
    const id = _.get(reward, "id");
    if (!id) return;
    dispatch(deleteReward({ id }));
  };

  const handleClose = () => {
    const id = _.get(reward, "id");
    if (id) {
      dispatch(invalidateDeleteRewardState(id));
    }
    onClose();
  };

  const rewardName = _.get(reward, "name");
  const title = _.trim(rewardName) ? rewardName : "Reward";

  return (
    <Slider.Dialog show={show} width="380px" onClose={handleClose}>
      <Slider.Header title="delete reward" message={title} />
      <Slider.Body>
        <div className={styles.deleteBody}>
          {!hasPendingRedemptions && (
          <div className={styles.message}>
            Are you sure you want to remove this reward from the catalog? This cannot be undone.
          </div>
          )}
          {hasPendingRedemptions && (
            <p className={styles.pendingWarning} role="alert">
              <i className="fa-solid fa-triangle-exclamation" aria-hidden />
              <span>This reward has pending redemptions and cannot be deleted.</span>
            </p>
          )}
        </div>
      </Slider.Body>
      <Slider.Actions>
        <Button
          variant="primary"
          icon="paperflite-tick"
          disabled={deleting || hasPendingRedemptions}
          onClick={handleConfirm}
        >
          {deleting ? "Deleting…" : "Confirm"}
        </Button>
        <Button disabled={deleting} onClick={handleClose}>Cancel</Button>
      </Slider.Actions>
    </Slider.Dialog>
  );
};